// Thin shell for the In-Room QR admin panel — each tab lives in its own file
// so the Babel visual-edits plugin doesn't stack-overflow traversing deeply
// nested JSX/map expressions.
import React, { useState } from 'react';
import { Loader2, Printer, ShoppingBag, Sparkles, Star, Utensils } from 'lucide-react';
import { API_URL, getToken } from './_shared';
import InRoomOrdersTab from './InRoomOrdersTab';
import InRoomRequestsTab from './InRoomRequestsTab';
import InRoomSurveysTab from './InRoomSurveysTab';
import InRoomMenuTab from './InRoomMenuTab';
import QRCardDesign from './QRCardDesign';

const QRSheetButton = () => {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/inroom/qr-sheet.pdf`, {
        credentials: 'include', headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error('Could not fetch QR sheet');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };
  return (
    <button
      onClick={download}
      disabled={loading}
      className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
      data-testid="inroom-print-qr-sheet"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
      Print QR sheet (one per room)
    </button>
  );
};

const TABS = [
  { k: 'orders', label: 'Orders', icon: ShoppingBag },
  { k: 'requests', label: 'Requests', icon: Sparkles },
  { k: 'surveys', label: 'Reviews', icon: Star },
  { k: 'menu', label: 'Menu', icon: Utensils },
];

const InRoomManager = ({ currentUser }) => {
  const [tab, setTab] = useState('orders');
  const isSuper = currentUser?.role === 'super_admin';

  const tabBtns = TABS.map(({ k, label, icon: Icon }) => (
    <button
      key={k}
      onClick={() => setTab(k)}
      className={`flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
        tab === k ? 'bg-orange-500 text-white' : 'text-slate-700 hover:bg-orange-50'
      }`}
      data-testid={`inroom-admin-tab-${k}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  ));

  return (
    <div className="space-y-4" data-testid="inroom-manager">
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-orange-500" /> In-room service
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Guests scan a QR posted in their room to order F&amp;B, request housekeeping and rate their stay.
            </p>
          </div>
          <QRSheetButton />
        </div>
      </div>

      {isSuper && <QRCardDesign />}

      <div className="flex gap-2 bg-white rounded-xl border p-1">{tabBtns}</div>

      {tab === 'orders' && <InRoomOrdersTab />}
      {tab === 'requests' && <InRoomRequestsTab />}
      {tab === 'surveys' && <InRoomSurveysTab />}
      {tab === 'menu' && <InRoomMenuTab />}
    </div>
  );
};

export default InRoomManager;
